// MUST be the first import — the Lingui singleton is activated
// SYNCHRONOUSLY inside this module so any subsequently-evaluated
// module that calls ``i18n._(msg`…`)`` at top scope (connector wizard
// specs build their ``CapabilityWizardSpec.title`` at import time) sees
// an active locale instead of crashing with
// ``Attempted to call a translation function without setting a locale``.
import { i18n, setupI18n } from "./i18n";

import { StrictMode, Suspense, lazy, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { I18nProvider } from "@lingui/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";

import { Toaster } from "@ando/ui";
import { AndoDialogProvider } from "@ando/ui/dialog";
import { apiClient } from "@ando/core";
import {
  refreshDesktopTokens,
  registerAuthFailureHandler,
  registerRefreshStrategy,
  registerSessionGateHandler,
  setDefaultAuthTokenProvider,
} from "@ando/core/api";
import { clearSession, registerDesktopSessionSource } from "@ando/core/auth";
import { getEnv } from "@ando/core";

import { getDesktopAccessToken } from "@/features/auth/desktop/useDesktopOAuth";
import { UpdateBanner } from "@/features/updates";
import {
  clearDesktopTokens,
  loadDesktopTokens,
  peekDesktopTokens,
  saveDesktopTokens,
} from "@/features/auth/desktop/tokenStore";

import "@ando/ui/styles.css";

// TEMP DEBUG (May 21, 2026) — log full stack when Radix emits a
// ``must be used within`` context warning so we can pinpoint the
// offending consumer in production. Remove once the v2 alpha smoke-test
// is clean.
const __origError = console.error;
console.error = (...args: unknown[]) => {
  const first = args[0];
  if (typeof first === "string" && first.includes("must be used within")) {
    __origError("[RADIX-TRAP]", ...args, new Error("radix-trap").stack);
    return;
  }
  // React #185 (Maximum update depth) trap — surface the component stack
  // so we can pinpoint the infinite-render culprit in production. The
  // minified message is opaque otherwise.
  if (
    (typeof first === "string" && (first.includes("Maximum update depth") || first.includes("Minified React error #185"))) ||
    (first instanceof Error && (first.message?.includes("Maximum update depth") || first.message?.includes("#185")))
  ) {
    // Dump every interesting carrier of context — Suppress on retries
    // (it'll fire 100× during the loop) by counting.
    (window as unknown as { __react185Hits?: number }).__react185Hits =
      ((window as unknown as { __react185Hits?: number }).__react185Hits ?? 0) + 1;
    const hits = (window as unknown as { __react185Hits?: number }).__react185Hits ?? 0;
    if (hits === 1) {
      // The crucial frames live in the ORIGINAL Error.stack carried in args.
      // React itself does ``console.error(err)`` where err is an Error
      // with a stack trace pointing at the component that triggered the
      // infinite update. Print it verbatim.
      for (let i = 0; i < args.length; i++) {
        const v = args[i];
        if (v instanceof Error) {
          __origError(`[REACT-185-TRAP arg#${i}] message:`, v.message);
          __origError(`[REACT-185-TRAP arg#${i}] stack:`, v.stack);
        } else {
          __origError(`[REACT-185-TRAP arg#${i}]:`, v);
        }
      }
      // console.trace dumps the FULL JS call chain at this point.
      // DevTools renders it with sourcemaps so we'll see real names.
      (console as unknown as { trace: (...a: unknown[]) => void }).trace(
        "[REACT-185-TRACE] full call chain at error",
      );
    } else if (hits <= 5) {
      __origError(`[REACT-185-TRAP #${hits}] suppressing duplicate`);
    }
    return;
  }
  __origError(...args);
};
window.addEventListener("error", (e) => {
  if (e.message?.includes("Minified React error #185") || e.message?.includes("Maximum update depth")) {
    console.error("[REACT-185-WINDOW]", e.message, e.error?.stack, e.filename, e.lineno);
  }
});

import { queryClient } from "./lib/queryClient";
import { router } from "./router";

// ── Wire core/api session callbacks into the TanStack Router shell ───────
//
// The ApiClient is target-agnostic: when a 401-refresh fails or a 403
// session-gate fires it just emits an event. The Vite/Tauri host decides
// the UX. Mirroring v1's web behaviour:
//   - refresh failure → clear the user_info cookie + push to /login
//   - account_inactive 403 → push to /login (BFF will re-render the
//     RequestAccessPage on the next hit)
//   - subscription_required 403 → currently treated as auth failure too;
//     once Phase 7 adds a /subscribe route we'll branch on the reason.
// Performed at module-eval time so the handlers are armed BEFORE any
// route runs ``beforeLoad``.
// On terminal auth failure (refresh rejected by Zitadel / cookie expired),
// wipe BOTH the ``user_info`` web cookie AND the OS-keychain/localStorage
// token bundle, otherwise the next ``_authed`` ``beforeLoad`` finds a
// stale ``refresh_token`` and loops back into the same 401 cascade
// (observed May 21, 2026 — refresh 400 → 401 every endpoint → app frozen
// without redirect because the local vault still ``looked`` authenticated).
const handleTerminalAuthFailure = (): void => {
  clearSession();
  if (getEnv().isTauri) {
    void clearDesktopTokens();
  }
  void router.navigate({ to: "/login" });
};

registerAuthFailureHandler(handleTerminalAuthFailure);
registerSessionGateHandler((reason) => {
  if (import.meta.env.DEV) {
    console.info("[api] session gate fired", { reason });
  }
  handleTerminalAuthFailure();
});

// ── Desktop (Tauri) Bearer-token transport ───────────────────────────────
//
// Native shells can't ride on the BFF httpOnly cookie (webview origin
// is a different cookie jar from the system browser that did the OAuth
// dance), so the ApiClient picks up the access token from the OS keychain
// via the desktop token store. The web build leaves the provider unset
// and the BFF cookie path stays unchanged.
//
// Wired here (module-eval) so the very first protected request — usually
// ``/auth/me`` triggered by the ``_authed`` route guard — already
// carries the Authorization header.
// Expose the auth bridge for editor primitives (Docx / Xlsx / PDF /
// PPTX preview) that download bytes via ``fetchSessionBytes`` in the
// ``@ando/ui/editors`` subpath. ``@ando/ui`` is leaf-package — it
// can't import ``@ando/core`` directly, so we ship the token getter
// through ``window.__ANDO_AUTH__``. The bridge is wired on BOTH web
// and Tauri so the editors work uniformly; on web the bearer header
// is ``null`` (cookie-only) and the call is a no-op.
(window as { __ANDO_AUTH__?: { getAuthToken: () => Promise<string | null> } }).__ANDO_AUTH__ = {
  getAuthToken: () => apiClient.getAuthToken(),
};

if (getEnv().isTauri) {
  // Process-wide default (NOT ``apiClient.setAuthTokenProvider``): the
  // Platform Console uses its own ``ApiClient`` bound to ``/platform`` and
  // any future transport must ride the same keychain Bearer. See
  // ``setDefaultAuthTokenProvider`` in ``@ando/core/api``.
  setDefaultAuthTokenProvider(() => getDesktopAccessToken());

  // Session reader for the ``_authed`` guard: tokens live in the OS
  // keychain (async), so ``@ando/core/auth`` reads the token store's
  // in-memory cache and awaits its one-shot hydrate (``hydrateSession``).
  registerDesktopSessionSource({
    peek: peekDesktopTokens,
    hydrate: loadDesktopTokens,
  });

  // 401 → desktop refresh path. Cookie endpoint ``/auth/refresh`` 401s
  // on Tauri (no cookie jar), causing an infinite ApiClient retry loop
  // unless we route refreshes through the OS-keychain-stored refresh
  // token. The strategy must persist the rotated tuple so the next
  // ``getAccessToken()`` returns the fresh Bearer.
  registerRefreshStrategy(async () => {
    const current = await loadDesktopTokens();
    if (!current?.refresh_token) {
      throw new Error("Desktop refresh: no refresh_token in vault");
    }
    const resp = await refreshDesktopTokens({
      refresh_token: current.refresh_token,
    });
    await saveDesktopTokens({
      access_token: resp.access_token,
      refresh_token: resp.refresh_token,
      id_token: resp.id_token,
      expires_at: Date.now() + resp.expires_in * 1000,
      user_info: resp.user_info,
    });
  });
}

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : () => null;

function App() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setupI18n()
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch((err) => {
        console.error("[i18n] catalog load failed", err);
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-cream text-ink-2" />
    );
  }

  return (
    <I18nProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <AndoDialogProvider>
          <RouterProvider router={router} />
        </AndoDialogProvider>
        <Toaster position="top-right" />
        {/* Auto-update banner — silent until an update is found. No-op
            outside Tauri (browser preview / vitest). Mount once at the
            root so the banner survives route changes. */}
        <UpdateBanner />
        {import.meta.env.DEV ? (
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
        ) : null}
      </QueryClientProvider>
    </I18nProvider>
  );
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("root element missing");

createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
